import { useState } from 'react'
import { Flame, Zap, Droplets, Beaker as BeakerIcon, Info, Check, FlaskConical } from 'lucide-react'
import { useLab } from '../../context/LabContext'
import { TestTube, Timer } from '../lab'

// Water samples from lab manual: (a) tap water, (b) 0.1 M NaCl, (c) distilled water,
// (d) laboratory reverse osmosis water, (e) spring water, (f) hydrogen ion exchange water,
// (g) Brita filtered water, and (h) possibly home tap water.
const sampleData = {
  tapWater: {
    name: '(a) Tap Water',
    conductivity: 'High',
    hardness: 'Medium',
    chloride: 'Present',
    flameColor: 'Yellow-Orange',
    flameElement: 'Sodium (Na)'
  },
  naclSolution: {
    name: '(b) 0.1 M NaCl',
    conductivity: 'Very High',
    hardness: 'None',
    chloride: 'Very High',
    flameColor: 'Yellow-Orange',
    flameElement: 'Sodium (Na)'
  },
  distilledWater: {
    name: '(c) Distilled Water',
    conductivity: 'Very Low',
    hardness: 'None',
    chloride: 'Absent',
    flameColor: 'No color',
    flameElement: 'None detected'
  },
  reverseOsmosisWater: {
    name: '(d) Reverse Osmosis Water',
    conductivity: 'Very Low',
    hardness: 'None',
    chloride: 'Absent',
    flameColor: 'No color',
    flameElement: 'None detected'
  },
  springWater: {
    name: '(e) Spring Water',
    conductivity: 'Medium',
    hardness: 'High',
    chloride: 'Present',
    flameColor: 'Lilac',
    flameElement: 'Potassium (K)'
  },
  hydrogenExchangeWater: {
    name: '(f) H⁺ Exchange Water',
    conductivity: 'Low',
    hardness: 'Very Low',
    chloride: 'Present',
    flameColor: 'No color',
    flameElement: 'None detected'
  },
  britaFilteredWater: {
    name: '(g) Brita Filtered Water',
    conductivity: 'Medium',
    hardness: 'Low',
    chloride: 'Present',
    flameColor: 'Yellow-Orange',
    flameElement: 'Sodium (Na)'
  },
  homeTapWater: {
    name: '(h) Home Tap Water',
    conductivity: 'Medium-High',
    hardness: 'Medium',
    chloride: 'Present',
    flameColor: 'Yellow-Orange',
    flameElement: 'Sodium (Na)'
  }
}

const testInfo = {
  conductivity: {
    title: 'Conductivity Test',
    why: 'Conductivity measures how well water conducts electricity, which indicates the presence of dissolved ions (electrolytes). Pure water has very low conductivity.',
    how: 'Electrodes measure electrical current flow through the sample. More ions = higher conductivity.',
    when: 'Use to quickly assess water purity and mineral content.'
  },
  hardness: {
    title: 'Hardness Test (EDTA Dropper Titration)',
    why: 'Water hardness is a measure of Ca²⁺ and Mg²⁺ salts. Hard water reduces soap effectiveness due to precipitation with calcium and magnesium.',
    how: 'Pipet 5.00 mL of sample into a large test tube. Add 4 drops ammonia-ammonium chloride buffer and 1 drop eriochrome black T indicator. Titrate with 0.00500 M EDTA using a dropper. Count drops for color change: pink-red → blue. Hardness (ppm) ≈ (5)(# of drops).',
    when: 'Perform on all water samples to compare purification effectiveness.'
  },
  chloride: {
    title: 'Chloride & Sulfate Tests (AgNO₃ / BaCl₂)',
    why: 'Tests for chloride and sulfate ions via precipitate formation.',
    how: 'Add a few drops of 0.1 M BaCl₂ to ~0.5 mL of each sample in 13×100 mm test tubes (sulfate test). Repeat with 0.1 M AgNO₃ on new samples (chloride test). Ag⁺ + Cl⁻ → AgCl↓. Ba²⁺ + SO₄²⁻ → BaSO₄↓.',
    when: 'Perform on all water samples to detect ion content.'
  },
  flame: {
    title: 'Flame Test',
    why: 'Different metal ions produce characteristic flame colors due to electron excitation.',
    how: 'Clean a platinum or nichrome wire by alternately dipping into 6 M HCl and holding in flame until no colour is observed. Put a few drops of each sample into clean test tubes. Test in flame, cleaning wire between each test. Do not put wire into original samples (HCl will contaminate).',
    when: 'Perform on all water samples to identify alkali and alkaline earth metals.'
  }
}

const Part2Analysis = () => {
  const { state, updatePart2, completeStep } = useLab()
  const { exp2Part2 } = state
  const [activeTest, setActiveTest] = useState(null)
  const [selectedSample, setSelectedSample] = useState('tapWater')
  const [testRunning, setTestRunning] = useState(false)
  const [showInfo, setShowInfo] = useState(null)
  const [flameActive, setFlameActive] = useState(false)
  const [results, setResults] = useState({})

  const runTest = (testType) => {
    if (testRunning) return

    setActiveTest(testType)
    setTestRunning(true)

    // Simulate test duration
    setTimeout(() => {
      const sample = sampleData[selectedSample]
      const newResults = { ...results }

      if (!newResults[selectedSample]) {
        newResults[selectedSample] = { name: sample.name }
      }

      switch (testType) {
        case 'conductivity':
          newResults[selectedSample].conductivity = sample.conductivity
          break
        case 'hardness':
          newResults[selectedSample].hardness = sample.hardness
          break
        case 'chloride':
          newResults[selectedSample].chloride = sample.chloride
          break
        case 'flame':
          newResults[selectedSample].flameColor = sample.flameColor
          newResults[selectedSample].flameElement = sample.flameElement
          setFlameActive(true)
          setTimeout(() => setFlameActive(false), 2000)
          break
      }

      setResults(newResults)
      updatePart2({ testResults: { ...exp2Part2.testResults, [testType]: { ...exp2Part2.testResults[testType], [selectedSample]: true } } })
      completeStep(`part2-${testType}-${selectedSample}`)
      setTestRunning(false)
      setActiveTest(null)
    }, 1500)
  }

  const getCompletedTests = () => {
    return Object.keys(results).reduce((count, sample) => {
      const r = results[sample]
      return count + (r.conductivity ? 1 : 0) + (r.hardness ? 1 : 0) +
             (r.chloride ? 1 : 0) + (r.flameColor ? 1 : 0)
    }, 0)
  }

  return (
    <div className="part2-container">
      {/* Sample Selection */}
      <div className="samples-panel">
        <h3 className="panel-title">Water Samples</h3>
        <div className="samples-grid">
          {Object.entries(sampleData).map(([key, sample]) => (
            <button
              key={key}
              className={`sample-btn ${selectedSample === key ? 'active' : ''}`}
              onClick={() => setSelectedSample(key)}
            >
              <TestTube
                fillLevel={80}
                fillColor={key === 'treatedWater' ? '#A8E6CF' : '#7DD3FC'}
                label=""
              />
              <span>{sample.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tests Panel */}
      <div className="tests-panel">
        <h3 className="panel-title">Analysis Tests</h3>
        <div className="tests-grid">
          <button
            className={`test-btn ${activeTest === 'conductivity' ? 'active' : ''}`}
            onClick={() => runTest('conductivity')}
            disabled={testRunning}
          >
            <div className="test-icon conductivity">
              <Zap size={24} />
            </div>
            <span>Conductivity</span>
            <button
              className="info-btn"
              onClick={(e) => { e.stopPropagation(); setShowInfo('conductivity'); }}
            >
              <Info size={14} />
            </button>
          </button>

          <button
            className={`test-btn ${activeTest === 'hardness' ? 'active' : ''}`}
            onClick={() => runTest('hardness')}
            disabled={testRunning}
          >
            <div className="test-icon hardness">
              <Droplets size={24} />
            </div>
            <span>Hardness (EDTA)</span>
            <button
              className="info-btn"
              onClick={(e) => { e.stopPropagation(); setShowInfo('hardness'); }}
            >
              <Info size={14} />
            </button>
          </button>

          <button
            className={`test-btn ${activeTest === 'chloride' ? 'active' : ''}`}
            onClick={() => runTest('chloride')}
            disabled={testRunning}
          >
            <div className="test-icon chloride">
              <BeakerIcon size={24} />
            </div>
            <span>Chloride (AgNO₃)</span>
            <button
              className="info-btn"
              onClick={(e) => { e.stopPropagation(); setShowInfo('chloride'); }}
            >
              <Info size={14} />
            </button>
          </button>

          <button
            className={`test-btn ${activeTest === 'flame' ? 'active' : ''}`}
            onClick={() => runTest('flame')}
            disabled={testRunning}
          >
            <div className="test-icon flame">
              <Flame size={24} />
            </div>
            <span>Flame Test</span>
            <button
              className="info-btn"
              onClick={(e) => { e.stopPropagation(); setShowInfo('flame'); }}
            >
              <Info size={14} />
            </button>
          </button>
        </div>

        {testRunning && (
          <div className="test-running">
            <Timer running={true} />
            <span>Running {testInfo[activeTest]?.title}...</span>
          </div>
        )}
      </div>

      {/* Lab Visualization */}
      <div className="lab-visual">
        <div className="bunsen-burner">
          <svg viewBox="0 0 120 200" width="120" height="200">
            <defs>
              <linearGradient id="burnerGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#4A4A4A" />
                <stop offset="50%" stopColor="#6A6A6A" />
                <stop offset="100%" stopColor="#4A4A4A" />
              </linearGradient>
              <linearGradient id="flameGrad" x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor="#FF5A36" />
                <stop offset="40%" stopColor="#FFEA00" />
                <stop offset="80%" stopColor="#7DD3FC" />
                <stop offset="100%" stopColor="#3B82F6" />
              </linearGradient>
              <filter id="flameGlow">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Base */}
            <ellipse cx="60" cy="185" rx="45" ry="10" fill="#333" />
            <rect x="20" y="140" width="80" height="45" rx="5" fill="url(#burnerGrad)" />

            {/* Tube */}
            <rect x="50" y="60" width="20" height="80" fill="url(#burnerGrad)" />
            <ellipse cx="60" cy="60" rx="10" ry="5" fill="#5A5A5A" />

            {/* Flame */}
            {flameActive && (
              <g filter="url(#flameGlow)">
                <path
                  d="M60 55 Q45 30 55 10 Q60 25 65 10 Q75 30 60 55"
                  fill="url(#flameGrad)"
                  opacity="0.9"
                >
                  <animate
                    attributeName="d"
                    values="M60 55 Q45 30 55 10 Q60 25 65 10 Q75 30 60 55;
                            M60 55 Q48 28 52 8 Q60 22 68 8 Q72 28 60 55;
                            M60 55 Q45 30 55 10 Q60 25 65 10 Q75 30 60 55"
                    dur="0.3s"
                    repeatCount="indefinite"
                  />
                </path>
              </g>
            )}

            {/* Gas inlet */}
            <rect x="85" y="150" width="25" height="8" rx="2" fill="#555" />
          </svg>
          <span className="equipment-label">Bunsen Burner</span>
        </div>

        <div className="current-sample">
          <TestTube
            fillLevel={70}
            fillColor={selectedSample === 'treatedWater' ? '#A8E6CF' : '#7DD3FC'}
            label={sampleData[selectedSample].name}
            active={testRunning}
          />
        </div>

        <div className="conductivity-meter">
          <svg viewBox="0 0 100 140" width="100" height="140">
            <defs>
              <linearGradient id="meterGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#3A3A3A" />
                <stop offset="50%" stopColor="#4A4A4A" />
                <stop offset="100%" stopColor="#3A3A3A" />
              </linearGradient>
            </defs>

            {/* Meter body */}
            <rect x="10" y="10" width="80" height="100" rx="8" fill="url(#meterGrad)" stroke="#555" strokeWidth="2" />

            {/* Display */}
            <rect x="20" y="20" width="60" height="35" rx="4" fill="#1A1A1A" />
            <text x="50" y="45" textAnchor="middle" fill={activeTest === 'conductivity' ? '#00FF00' : '#00AA00'} fontSize="14" fontFamily="monospace">
              {activeTest === 'conductivity' ? '---' : results[selectedSample]?.conductivity || '0.00'}
            </text>
            <text x="50" y="52" textAnchor="middle" fill="#666" fontSize="6">µS/cm</text>

            {/* Probes */}
            <rect x="35" y="110" width="8" height="30" rx="2" fill="#888" />
            <rect x="57" y="110" width="8" height="30" rx="2" fill="#888" />
            <circle cx="39" cy="135" r="3" fill="#FFD700" />
            <circle cx="61" cy="135" r="3" fill="#FFD700" />
          </svg>
          <span className="equipment-label">Conductivity Meter</span>
        </div>
      </div>

      {/* Results Table */}
      <div className="results-panel">
        <h3 className="panel-title">
          <FlaskConical size={18} />
          Analysis Results
          <span className="results-count">{getCompletedTests()}/32 tests</span>
        </h3>
        <div className="results-table-wrapper">
          <table className="results-table">
            <thead>
              <tr>
                <th>Sample</th>
                <th>Conductivity</th>
                <th>Hardness</th>
                <th>Chloride</th>
                <th>Flame Color</th>
                <th>Element</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(sampleData).map(([key, sample]) => {
                const result = results[key] || {}
                return (
                  <tr key={key} className={selectedSample === key ? 'active' : ''}>
                    <td className="sample-name">{sample.name}</td>
                    <td>{result.conductivity || <span className="pending">-</span>}</td>
                    <td>{result.hardness || <span className="pending">-</span>}</td>
                    <td>{result.chloride || <span className="pending">-</span>}</td>
                    <td>{result.flameColor || <span className="pending">-</span>}</td>
                    <td>{result.flameElement || <span className="pending">-</span>}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Modal */}
      {showInfo && (
        <div className="info-overlay" onClick={() => setShowInfo(null)}>
          <div className="info-modal" onClick={e => e.stopPropagation()}>
            <h4>{testInfo[showInfo].title}</h4>
            <div className="info-section">
              <span className="info-label why">WHY?</span>
              <p>{testInfo[showInfo].why}</p>
            </div>
            <div className="info-section">
              <span className="info-label how">HOW?</span>
              <p>{testInfo[showInfo].how}</p>
            </div>
            <div className="info-section">
              <span className="info-label when">WHEN?</span>
              <p>{testInfo[showInfo].when}</p>
            </div>
            <button className="info-close" onClick={() => setShowInfo(null)}>Got It!</button>
          </div>
        </div>
      )}

      <style>{`
        .part2-container {
          display: grid;
          grid-template-columns: 200px 1fr 420px;
          grid-template-rows: auto 1fr;
          gap: 16px;
          height: 100%;
          padding: 16px;
        }

        .samples-panel {
          grid-row: span 2;
          background: var(--bg-surface);
          border-radius: 12px;
          padding: 16px;
          border: 1px solid var(--border);
        }

        .panel-title {
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .samples-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .sample-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 10px;
          border: 2px solid transparent;
          transition: all 0.2s;
          text-align: left;
        }

        .sample-btn:hover {
          border-color: var(--primary);
        }

        .sample-btn.active {
          border-color: var(--primary);
          background: rgba(255, 90, 54, 0.15);
        }

        .sample-btn span {
          font-size: 12px;
          font-weight: 500;
        }

        .tests-panel {
          background: var(--bg-surface);
          border-radius: 12px;
          padding: 16px;
          border: 1px solid var(--border);
        }

        .tests-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .test-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 16px 12px;
          background: var(--bg-elevated);
          border-radius: 10px;
          border: 2px solid transparent;
          transition: all 0.2s;
          position: relative;
        }

        .test-btn:hover:not(:disabled) {
          border-color: var(--primary);
          transform: translateY(-2px);
        }

        .test-btn.active {
          border-color: var(--primary);
          background: rgba(255, 90, 54, 0.2);
        }

        .test-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .test-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .test-icon.conductivity { background: linear-gradient(135deg, #3B82F6, #1D4ED8); }
        .test-icon.hardness { background: linear-gradient(135deg, #8B5CF6, #6D28D9); }
        .test-icon.chloride { background: linear-gradient(135deg, #10B981, #047857); }
        .test-icon.flame { background: linear-gradient(135deg, #FF5A36, #DC2626); }

        .test-btn span {
          font-size: 11px;
          font-weight: 600;
        }

        .info-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--bg-surface);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
          transition: all 0.2s;
        }

        .info-btn:hover {
          background: var(--primary);
          color: white;
        }

        .test-running {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-top: 16px;
          padding: 12px;
          background: rgba(255, 90, 54, 0.1);
          border-radius: 8px;
          color: var(--primary);
          font-weight: 500;
        }

        .lab-visual {
          background: var(--bg-surface);
          border-radius: 12px;
          padding: 16px;
          border: 1px solid var(--border);
          display: flex;
          justify-content: space-around;
          align-items: flex-end;
        }

        .bunsen-burner,
        .current-sample,
        .conductivity-meter {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .equipment-label {
          font-size: 11px;
          color: var(--text-secondary);
        }

        .results-panel {
          grid-row: span 2;
          background: var(--bg-surface);
          border-radius: 12px;
          padding: 16px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
        }

        .results-count {
          margin-left: auto;
          font-size: 12px;
          color: var(--primary);
          background: rgba(255, 90, 54, 0.15);
          padding: 4px 10px;
          border-radius: 12px;
        }

        .results-table-wrapper {
          flex: 1;
          overflow: auto;
        }

        .results-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        .results-table th,
        .results-table td {
          padding: 10px 8px;
          text-align: left;
          border-bottom: 1px solid var(--border);
        }

        .results-table th {
          background: var(--bg-elevated);
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          color: var(--text-secondary);
        }

        .results-table tr.active {
          background: rgba(255, 90, 54, 0.1);
        }

        .sample-name {
          font-weight: 600;
        }

        .pending {
          color: var(--text-muted);
        }

        .info-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .info-modal {
          background: var(--bg-surface);
          border-radius: 16px;
          padding: 24px;
          width: 420px;
          max-width: 90vw;
          border: 2px solid var(--primary);
        }

        .info-modal h4 {
          font-size: 18px;
          margin-bottom: 16px;
        }

        .info-section {
          background: var(--bg-elevated);
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 12px;
        }

        .info-label {
          display: inline-block;
          font-size: 10px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 4px;
          margin-bottom: 6px;
        }

        .info-label.why { background: var(--accent); color: #1A1A1A; }
        .info-label.how { background: var(--secondary); color: white; }
        .info-label.when { background: var(--primary); color: white; }

        .info-section p {
          font-size: 13px;
          line-height: 1.5;
          color: var(--text-primary);
        }

        .info-close {
          width: 100%;
          padding: 12px;
          background: var(--primary);
          color: white;
          border-radius: 8px;
          font-weight: 600;
          margin-top: 8px;
        }

        .info-close:hover {
          background: var(--primary-dark);
        }
      `}</style>
    </div>
  )
}

export default Part2Analysis
